import {
  Button,
  ButtonGroup,
  Flex,
  Select,
  Spinner,
  Stack,
  Text,
} from "@chakra-ui/react";
import { useMemo } from "react";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import {
  ContractOverviewFigure,
  ContractReportOptionParameter,
  useGetContractOverviewQuery,
  useListContractReportOptionsQuery,
  type ContractFigureAnnualValueDto,
  type ContractOverviewDto,
  type ContractOverviewFigureDto,
} from "../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../common/auth/useLoggedInWithOrgContextState";
import { FilterChip } from "../../common/components/FilterChip";
import { ListLoadError } from "../../common/ListLoadError";
import { useApiError } from "../../common/errors/useApiError";
import { displaySum } from "../../common/formatting/displaySum";
import { formatNumber } from "../../common/formatting/formatNumber";
import { base64Encode } from "../../common/utils/base64";
import { ContractSigning } from "./ContractSigning";
import { FrameworkOverview } from "./FrameworkOverview";
import { MissingContractData } from "./MissingContractData";
import { OverviewFigureGrid, OverviewFigureTile } from "./OverviewFigureTile";
import { ValuePerSupplier } from "./ValuePerSupplier";
import { ContractExpiryTimeline } from "./ContractExpiryTimeline";
import { UpcomingOptionDeadlines } from "./UpcomingOptionDeadlines";
import {
  contractScopeText,
  FIGURE_ORDER,
  figureDefinition,
  figureLabel,
  figureLink,
  overviewQuery,
  type ContractScope,
  type OverviewSelection,
} from "./contractFigures";

export const ContractsOverview = () => {
  const { t } = useTranslation();
  const authState = useLoggedInWithOrgContextState();
  const organizationId = authState.selectedOrg.id;
  const projects = useProjectOptions(organizationId);
  const [selection, choose] = useSelectionInAddress(projects.data?.options.map((project) => project.value));
  const base = useMemo(() => overviewQuery(organizationId, selection), [organizationId, selection]);
  const q = base64Encode(JSON.stringify(base));
  const { data, isLoading, isFetching, error, refetch } = useGetContractOverviewQuery({ q });
  const { message } = useApiError();

  return (
    <Stack spacing="5">
      <Flex gap="3" wrap="wrap" align="center">
        <ButtonGroup size="sm" isAttached={false} spacing="2">
          <FilterChip
            active={selection.scope === "all"}
            label={String(t("All contracts"))}
            onClick={() => choose({ scope: "all" })}
          />
          <FilterChip
            active={selection.scope === "frameworkAgreements"}
            label={String(t("Framework agreements"))}
            onClick={() => choose({ scope: "frameworkAgreements" })}
          />
        </ButtonGroup>
        <ProjectChoice
          projects={projects}
          projectId={selection.projectId}
          onChange={(projectId) => choose({ projectId })}
        />
        {isFetching && !isLoading && <Spinner size="sm" color="smPrimary" />}
      </Flex>
      {isLoading ? (
        <Spinner color="smPrimary" />
      ) : error || !data ? (
        <ListLoadError message={message(error)} onRetry={() => void refetch()} isRetrying={isFetching} />
      ) : (
        <>
          <Figures overview={data} base={base} />
          <ValuePerSupplier overview={data} base={base} />
          <MissingContractData overview={data} base={base} />
          <ContractExpiryTimeline overview={data} base={base} />
          <UpcomingOptionDeadlines overview={data} base={base} />
        </>
      )}
      <ContractSigning q={q} base={base} />
      <FrameworkOverview projectId={selection.projectId} />
    </Stack>
  );
};

const PROJECT_PARAM = "project";
const CONTRACTS_PARAM = "contracts";
const FRAMEWORK_AGREEMENTS = "framework";

const useProjectOptions = (organizationId: string) =>
  useListContractReportOptionsQuery({
    q: base64Encode(JSON.stringify({ owningOrganizationId: organizationId })),
    parameterName: ContractReportOptionParameter.ProjectId,
  });

const useSelectionInAddress = (
  knownProjectIds: string[] | undefined
): [OverviewSelection, (change: Partial<OverviewSelection>) => void] => {
  const [params, setParams] = useSearchParams();
  const scope = params.get(CONTRACTS_PARAM) === FRAMEWORK_AGREEMENTS ? "frameworkAgreements" : "all";
  const requestedProjectId = params.get(PROJECT_PARAM) || null;
  const projectId =
    requestedProjectId && knownProjectIds && !knownProjectIds.includes(requestedProjectId) ? null : requestedProjectId;
  const selection = useMemo<OverviewSelection>(() => ({ scope, projectId }), [scope, projectId]);
  const choose = (change: Partial<OverviewSelection>) =>
    setParams(
      (current) => {
        const next = { ...selection, ...change };
        const updated = new URLSearchParams(current);
        if (next.scope === "frameworkAgreements") updated.set(CONTRACTS_PARAM, FRAMEWORK_AGREEMENTS);
        else updated.delete(CONTRACTS_PARAM);
        if (next.projectId) updated.set(PROJECT_PARAM, next.projectId);
        else updated.delete(PROJECT_PARAM);
        return updated;
      },
      { replace: true }
    );
  return [selection, choose];
};

const ProjectChoice = ({
  projects,
  projectId,
  onChange,
}: {
  projects: ReturnType<typeof useProjectOptions>;
  projectId: string | null;
  onChange: (projectId: string | null) => void;
}) => {
  const { t } = useTranslation();
  return (
    <Flex gap="2" align="center" wrap="wrap">
      <Select
        size="sm"
        maxW="64"
        aria-label={String(t("Project"))}
        value={projectId ?? ""}
        isDisabled={projects.isLoading || projects.isError}
        onChange={(event) => onChange(event.target.value || null)}
      >
        <option value="">{projects.isLoading ? t("Loading projects…") : t("All projects")}</option>
        {(projects.data?.options ?? []).map((project) => (
          <option key={project.value} value={project.value}>
            {project.label}
          </option>
        ))}
      </Select>
      {projects.isError && (
        <Text fontSize="sm" color="smMuted">
          {t("Could not load the projects.")}{" "}
          <Button variant="link" size="sm" onClick={() => void projects.refetch()}>
            {t("Try again")}
          </Button>
        </Text>
      )}
    </Flex>
  );
};

const Figures = ({ overview, base }: { overview: ContractOverviewDto; base: ReturnType<typeof overviewQuery> }) => {
  const { t } = useTranslation();
  const byFigure = new Map(overview.figures.map((figure) => [figure.figure, figure]));
  const activeContracts = byFigure.get(ContractOverviewFigure.Active)?.value ?? 0;
  const frameworkScope = !!base.activeFrameworkAgreement;
  return (
    <Stack spacing="4">
      <OverviewFigureGrid columns={{ base: 1, sm: 2, lg: 4 }}>
        {FIGURE_ORDER.map((key) => {
          const figure = byFigure.get(key);
          return figure ? (
            <FigureTile key={key} figure={figure} overview={overview} activeContracts={activeContracts} base={base} />
          ) : null;
        })}
      </OverviewFigureGrid>
      <Stack spacing="1" fontSize="sm" color="smMuted">
        <Text>{t("Based on what you have access to: {{scope}}", { scope: contractScopeText(t, overview.scope) })}</Text>
        {overview.activeWithoutEndDate > 0 && (
          <Text>
            {frameworkScope
              ? t(
                  "{{missing}} of {{total}} suppliers in active framework agreements have no end date and are not included in «Expiring within 90 days».",
                  { missing: formatNumber(overview.activeWithoutEndDate), total: formatNumber(activeContracts) },
                )
              : t("{{missing}} of {{total}} active contracts have no end date and are not included in «Expiring within 90 days».", {
                  missing: formatNumber(overview.activeWithoutEndDate),
                  total: formatNumber(activeContracts),
                })}
          </Text>
        )}
        {overview.activeWithoutAnnualValue > 0 && (
          <Text>
            {frameworkScope
              ? t(
                  "{{missing}} of {{total}} suppliers in active framework agreements have no annual value and are not included in «Total annual value».",
                  { missing: formatNumber(overview.activeWithoutAnnualValue), total: formatNumber(activeContracts) },
                )
              : t("{{missing}} of {{total}} active contracts have no annual value and are not included in «Total annual value».", {
                  missing: formatNumber(overview.activeWithoutAnnualValue),
                  total: formatNumber(activeContracts),
                })}
          </Text>
        )}
        {overview.activeInOtherCurrency > 0 && (
          <Text>
            {frameworkScope
              ? t(
                  "{{count}} suppliers in active framework agreements in another currency than {{currency}} are not included in «Total annual value».",
                  { count: overview.activeInOtherCurrency, currency: overview.annualValueCurrency },
                )
              : t("{{count}} active contracts in another currency than {{currency}} are not included in «Total annual value».", {
                  count: overview.activeInOtherCurrency,
                  currency: overview.annualValueCurrency,
                })}
          </Text>
        )}
      </Stack>
    </Stack>
  );
};

const FigureTile = ({
  figure,
  overview,
  activeContracts,
  base,
}: {
  figure: ContractOverviewFigureDto;
  overview: ContractOverviewDto;
  activeContracts: number;
  base: ReturnType<typeof overviewQuery>;
}) => {
  const { t } = useTranslation();
  const scope: ContractScope = base.activeFrameworkAgreement ? "frameworkAgreements" : "all";
  const isValue = figure.figure === ContractOverviewFigure.AnnualValue;
  const shown = isValue
    ? displaySum({ value: Math.round(figure.value), currency: overview.annualValueCurrency })
    : formatNumber(figure.value);

  return (
    <OverviewFigureTile
      label={figureLabel(t, figure.figure, scope)}
      shown={shown}
      below={
        <>
          <Text fontSize="xs" color="smMuted">
            {changeText(t, figure, isValue)}
          </Text>
          {!!figure.drafts && (
            <Text fontSize="xs" color="smMuted">
              {t("{{count}} of them are drafts", { count: figure.drafts })}
            </Text>
          )}
          {figure.figure === ContractOverviewFigure.AwaitingSignature && overview.awaitingSignaturePastDeadline > 0 && (
            <Text fontSize="xs" color="smError">
              {t("{{count}} past the signing deadline", { count: overview.awaitingSignaturePastDeadline })}
            </Text>
          )}
        </>
      }
      definition={figureDefinition(t, figure.figure, scope)}
      details={details(t, figure, overview, shown, activeContracts, scope)}
      link={figureLink(base, figure)}
      testId={figure.figure}
    />
  );
};

const changeText = (t: TFunction, figure: ContractOverviewFigureDto, isValue: boolean): string => {
  if (figure.lastMonth == null) return t("No comparison with last month");
  const change = figure.value - figure.lastMonth;
  if (change === 0) return t("No change since last month");
  return t("{{change}} since last month", {
    change: `${change > 0 ? "+" : "−"}${formatNumber(Math.abs(isValue ? Math.round(change) : change))}`,
  });
};

const details = (
  t: TFunction,
  figure: ContractOverviewFigureDto,
  overview: ContractOverviewDto,
  shown: string,
  activeContracts: number,
  scope: ContractScope,
): string => {
  if (figure.lastMonth == null) {
    return t(
      "{{count}} contracts. There is no comparison with last month, because the product does not keep a history of which contracts were waiting for signing or had no owner.",
      { count: figure.value },
    );
  }
  if (figure.figure === ContractOverviewFigure.AnnualValue) {
    const counted = formatNumber(overview.annualValueContracts);
    const lastMonth = displaySum({ value: Math.round(figure.lastMonth), currency: overview.annualValueCurrency });
    return scope === "frameworkAgreements"
      ? t("{{value}} from {{counted}} of {{count}} suppliers in active framework agreements. Last month: {{lastMonth}}.", {
          value: shown,
          counted,
          count: activeContracts,
          lastMonth,
        })
      : t("{{value}} from {{counted}} of {{count}} active contracts. Last month: {{lastMonth}}.", {
          value: shown,
          counted,
          count: activeContracts,
          lastMonth,
        });
  }
  const counted = t("{{count}} contracts. Last month: {{lastMonth}}.", {
    count: figure.value,
    lastMonth: formatNumber(figure.lastMonth),
  });
  return figure.annualValue ? `${counted} ${annualValueText(t, figure.annualValue, overview.annualValueCurrency)}` : counted;
};

const annualValueText = (t: TFunction, annual: ContractFigureAnnualValueDto, currency: string): string => {
  const value = `${t("Annual value: {{value}}", { value: displaySum({ value: Math.round(annual.value), currency }) })}.`;
  if (annual.notCounted === 0) return value;
  return `${value} ${t("{{count}} without an annual value in {{currency}} are not in the value.", {
    count: annual.notCounted,
    currency,
  })}`;
};
